























































import Vue from "vue";
import { Location } from "vue-router";
import { AudioPlayerStatus } from "@joyz/app-core/models/audio";
import {
  ROUTES,
  EXERCISE_TYPE,
  VUEX_ASSIGNMENT_ACTION_NAMES,
  VUEX_ASSIGNMENT_NAMESPACE,
  VUEX_ROOT_ACTION_NAMES,
  VUEX_ROOT_GETTER_NAMES
} from "@tt-app/constants";
import {
  ApiExerciseBtnAction,
  ChatAllObjects,
  ChatState,
  ExerciseViewData,
  ExerciseViewMethods
} from "@tt-app/models/exercise";
import {
  chatHandler,
  exerciseHandler,
  exerciseNavHandler,
  FlatExerciseListDetails,
  getNewExerciseViewState,
  isNonTrophyAction,
  nextExerciseRouter
} from "@tt-app/utils";
import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";

const { DETAILS_REQUEST } = VUEX_ASSIGNMENT_ACTION_NAMES;
const { FETCH_DETAILS } = VUEX_ROOT_ACTION_NAMES;
const { FLAT_EXERCISE_LIST } = VUEX_ROOT_GETTER_NAMES;

import AssignmentCompletionModal from "@tt-app/components/assignment/AssignmentCompletionModal.vue";
import ChatBody from "@tt-app/components/exercise/ChatBody.vue";
import ExerciseHeaderTitle from "@tt-app/components/exercise/ExerciseHeaderTitle.vue";
import LayoutExercise from "@tt-app/layouts/LayoutExercise.vue";

import IconDict from "@joyz/app-core/assets/icons/default/Dictionary.svg";
import {
  Session,
  TerratalkLiveGetterNames,
  TerratalkLiveNamespace
} from "@tt-app/store/terratalkLive/types";
import { audioContext } from "@tt-app/utils/audio/audioContext";

interface Props {
  iconDict: string;
}

interface Data extends ExerciseViewData {
  audioDecoderPlayer: AudioDecoderPlayer;
  chat: ChatState;
  /**
   * Audio player for the main demo audio replay
   */
  demoAudioPlayer: AudioDecoderPlayer;
  /**
   * Flag to follow the pause/resume status. If the user pauses exactly between
   * audio file play (250ms window by default), the audio player is already at
   * "ready" status so it will not paused. The onMsgDisplayEnded callback must
   * then be explicitely stopped
   */
  demoInterrupted: boolean;
  demoStarted: boolean;
  /**
   * Audio player for specific bubble replay. Audio player must be
   * duplicated so that the main audio stream, the demo, can be
   * paused
   */
  replayAudioPlayer: AudioDecoderPlayer;
}

interface Methods extends ExerciseViewMethods {
  fetchAssignmentDetails: () => void;
  fetchDetails: () => void;
  initChat: () => Promise<void>;
  showNextChatMessage: () => void;
  onActionBtnClick: (action: ApiExerciseBtnAction) => void;
  onDictionaryClick: () => void;
  onDemoPause: () => void;
  onDemoResume: () => void;
  onDemoRestart: () => void;
  onMsgDisplayEnded: () => (newChatState: ChatState) => Promise<void>;
  refreshAudioPlayer: () => void;
  updateFooter: () => Promise<void>;
}

interface Computed {
  assignmentId?: string;
  assignmentsCompleted: number;
  courseUuid: string;
  currentExerciseListIndex: number | null;
  demoAudioStatus: AudioPlayerStatus;
  exerciseUuid: string;
  feedbackRouteParams: {
    withFeedback: string;
    logId: string;
  };
  flatExerciseList: FlatExerciseListDetails[];
  limitedModeActive: boolean;
  nextExercise?: FlatExerciseListDetails;
  nextExerciseButtonText: string;
  session: Session;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "Chat",
  components: { AssignmentCompletionModal, ExerciseHeaderTitle, ChatBody, LayoutExercise },

  props: {
    iconDict: { type: String, default: IconDict }
  },

  data() {
    const audioDecoderPlayer = new AudioDecoderPlayer({ audioContext });

    return {
      ...getNewExerciseViewState(EXERCISE_TYPE.DEMO_LISTENING, {
        name: ROUTES.COURSE.DETAILS.NAME,
        params: { courseUuid: this.$route.params.courseUuid }
      }),
      audioDecoderPlayer: audioDecoderPlayer,
      chat: chatHandler.getNewState(),
      demoAudioPlayer: audioDecoderPlayer,
      demoInterrupted: false,
      demoStarted: false,
      replayAudioPlayer: audioDecoderPlayer
    };
  },

  computed: {
    assignmentId() {
      return this.$route.params.assignmentId;
    },
    assignmentsCompleted() {
      const completedCount = this.exercise?.results?.trophyInfo?.numAssignmentsSubmitted;
      return completedCount || 0;
    },
    courseUuid() {
      return this.$route.params.courseUuid;
    },
    currentExerciseListIndex() {
      const listIndex = this.flatExerciseList.findIndex(
        (exerciseDetails: FlatExerciseListDetails) => {
          return exerciseDetails.uuid === this.exerciseUuid;
        }
      );

      return listIndex >= 0 ? listIndex : null;
    },
    demoAudioStatus() {
      return this.demoInterrupted || this.demoAudioPlayer.status === "paused"
        ? "paused"
        : "playing";
    },
    exerciseUuid() {
      return this.$route.params.exerciseUuid;
    },
    feedbackRouteParams() {
      const exerciseStarted = this.exercise.logId > 0;
      const exerciseFinished = this.exercise.status === "finished";

      return {
        withFeedback: Boolean(exerciseStarted && !exerciseFinished).toString(),
        logId: this.exercise.logId.toString()
      };
    },
    flatExerciseList() {
      return this.$store.getters[FLAT_EXERCISE_LIST]({
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid
      });
    },
    limitedModeActive() {
      return Boolean(this.session?.limitedMode?.id);
    },
    nextExercise() {
      if (this.currentExerciseListIndex === null) return undefined;
      return this.flatExerciseList[this.currentExerciseListIndex + 1];
    },
    nextExerciseButtonText() {
      let i18nString = "exercise.end";

      if (this.nextExercise) {
        i18nString = "exercise.next";
      }

      return this.$__(i18nString);
    },
    session() {
      return this.$store.getters[
        `${TerratalkLiveNamespace}/${TerratalkLiveGetterNames.ACTIVE_SESSION}`
      ];
    }
  },

  watch: {
    "nav.requireExitConfirmation": {
      handler(newValue) {
        window.onbeforeunload = null;

        if (newValue) {
          window.onbeforeunload = () => {
            return true;
          };
        }
      }
    },
    "chat.display.lastItem": {
      async handler(lastItem: ChatAllObjects, oldItem: ChatAllObjects) {
        if (oldItem === lastItem) {
          return;
        }

        // Ensure that footer buttons are visible
        if (isNonTrophyAction(lastItem)) {
          await this.updateFooter();
        }

        if (lastItem.type === "SHOW_END_BUTTONS") {
          this.exercise.status = "finished";
          this.nav.requireExitConfirmation = false;
          this.chat.allowUserInteraction = true;
          this.exercise = await exerciseHandler.endExercise(this, this.exercise, {
            incorrectCount: 0,
            correctCount: 0
          });
        }
      }
    }
  },

  async created() {
    this.fetchDetails();
    // Safari specific: sometimes, the AudioContext is not even initialised
    if (!this.demoAudioPlayer.audioContext) {
      this.demoAudioPlayer = this.audioDecoderPlayer;
    }
    if (!this.replayAudioPlayer.audioContext) {
      this.replayAudioPlayer = this.audioDecoderPlayer;
    }

    await this.initChat();

    document.addEventListener("visibilitychange", this.refreshAudioPlayer);
  },

  beforeDestroy() {
    this.audioDecoderPlayer.destroy();

    this.demoInterrupted = true;

    document.removeEventListener("visibilitychange", this.refreshAudioPlayer);
  },

  beforeRouteEnter(to, from, next) {
    next((vm: Vue) => {
      vm.$data.exercise = exerciseHandler.checkAssignmentId(vm.$data.exercise, from);
    });
  },

  // https://router.vuejs.org/guide/advanced/navigation-guards.html#in-component-guards
  async beforeRouteLeave(to, from, next) {
    exerciseNavHandler.beforeRouteLeave(this.nav, state => (this.nav = state), to, from, next);
  },

  methods: {
    exit() {
      exerciseNavHandler.exit(this, this.nav, state => (this.nav = state), {
        feedbackParams: this.feedbackRouteParams
      });
    },

    async fetchAssignmentDetails() {
      await this.$store.dispatch(`${VUEX_ASSIGNMENT_NAMESPACE}/${DETAILS_REQUEST}`, {
        assignmentId: this.$route.params.assignmentId
      });
    },

    fetchDetails() {
      this.$store.dispatch(`${FETCH_DETAILS}`, {
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid
      });
    },

    onExitCancelled() {
      this.nav = exerciseNavHandler.onExitCancelled(this.nav);
    },

    onExitConfirmed() {
      exerciseNavHandler.onExitConfirm(this, this.nav, state => (this.nav = state), {
        feedbackParams: this.feedbackRouteParams
      });
    },

    async initChat() {
      this.demoInterrupted = true;

      // (Re-)init states
      let fallback: Location;
      if (this.assignmentId) {
        await this.fetchAssignmentDetails();
      }

      if (this.$route.params.assignmentId && this.$store.state.assignment.details) {
        fallback = {
          name: ROUTES.ASSIGNMENT.DETAILS.NAME,
          params: {
            assignmentId: this.$route.params.assignmentId
          }
        };
      } else {
        fallback = {
          name: ROUTES.COURSE.DETAILS.NAME,
          params: {
            courseUuid: this.$route.params.courseUuid
          }
        };
      }

      const newState = getNewExerciseViewState(EXERCISE_TYPE.DEMO_LISTENING, fallback);
      this.exercise = newState.exercise;
      this.nav = newState.nav;

      this.chat = chatHandler.getNewState();
      this.chat.id = new Date().toISOString(); // unique enough for our needs

      // Load chat
      this.exercise.status = "loading";
      try {
        this.chat = await chatHandler.loadData(this, this.chat, this.exercise);
      } catch (err) {
        this.loadingError = this.$__("chat.error.loading");
        this.exercise.status = "error";
        return;
      }
      if (Number(this.assignmentId)) {
        this.exercise.assignmentId = Number(this.assignmentId);
      }
      this.exercise.status = "loaded";

      // Update states after data loading
      this.exercise = exerciseHandler.loadHeaderFromChat(this.exercise, this.chat);

      this.exercise = await exerciseHandler.startExercise(this, this.exercise);

      // Start the chat
      this.exercise.status = "in-progress";
      this.demoInterrupted = false;
      this.showNextChatMessage();
    },

    async onActionBtnClick(action) {
      // Start action
      if (action === "START") {
        // Immediately hide the footer buttons to avoid rapid-fire clicking. This
        // must be handled in the view, not in the handlers, for immediate reactivity
        this.exercise.footer.buttons = [];

        this.nav.requireExitConfirmation = true;

        this.demoStarted = true;
        this.showNextChatMessage();
      }
      // Motivated user wants to start again
      else if (action === "RETRY") {
        await this.onDemoRestart();
      }
      // Non-motivated user wants to get out here as soon as possible
      else if (action === "NEXT_EXERCISE") {
        nextExerciseRouter({
          assignmentId: this.assignmentId,
          courseUuid: this.courseUuid,
          ...this.nextExercise
        });
      }
    },

    showNextChatMessage() {
      chatHandler.moveToNextMessage({
        state: this.chat,
        audioDecoderPlayer: this.demoAudioPlayer,
        onMsgDisplayStarted: async newState => {
          this.chat = newState;
          this.updateFooter();
        },
        onMsgDisplayEnded: this.onMsgDisplayEnded()
      });
    },

    onDictionaryClick() {
      window.alert("onDictionaryClick is TODO");
    },

    onDemoPause() {
      // If some audio was being played, it will be paused...
      this.demoAudioPlayer.pause();
      // ...otherwise, this flag must ensure that the demo conversation is paused
      this.demoInterrupted = true;

      this.chat.allowUserInteraction = true;
      this.chat.audioPlay.currentlyPlayedIndex = -1;
    },

    onDemoResume() {
      this.chat.allowUserInteraction = false;

      // If the user was replaying some audio, stop it
      this.replayAudioPlayer.stop();
      // If some audio was paused, resume it. This is the part why two audio player is
      // required.
      this.demoAudioPlayer.resume();

      this.demoInterrupted = false;

      if (this.demoAudioPlayer.status === "ready") {
        this.showNextChatMessage();
      } else if (this.demoAudioPlayer.status === "playing") {
        this.chat.audioPlay.currentlyPlayedIndex = this.chat.messages.toDisplay.length - 1;
      }

      this.updateFooter();
    },

    onDemoRestart() {
      // Silence is gold
      this.replayAudioPlayer.stop();
      this.demoAudioPlayer.stop();

      this.onDemoPause();
      this.initChat();
    },

    onMsgDisplayEnded() {
      if (this.demoInterrupted) {
        return async () => {
          // do nothing
        };
      }

      return async (newState: ChatState) => {
        // If IDs mismatch, both state are not belonging to the same session
        if (this.chat.id && this.chat.id !== newState.id) {
          return;
        }

        this.chat = newState;

        const chatStatus = this.chat.status;
        if (chatStatus !== "IntroWaitForUser" && chatStatus !== "Ended") {
          this.showNextChatMessage();
        }
      };
    },

    refreshAudioPlayer() {
      if (document.visibilityState === "visible") {
        console.log("refreshing audio player");
        const audioDecoderPlayer = new AudioDecoderPlayer({ audioContext });

        this.audioDecoderPlayer = audioDecoderPlayer;
        this.demoAudioPlayer = audioDecoderPlayer;
        this.replayAudioPlayer = audioDecoderPlayer;
      }
    },

    async updateFooter() {
      this.exercise = exerciseHandler.loadFooterFromChat(this, this.exercise, this.chat);

      // in demo conversation, footer is never disabled
      this.exercise.footer.disabled = false;

      if (this.demoStarted) {
        const chatBody = (this.$refs as any)["chat-body"];
        if (chatBody) {
          await (chatBody as any).scrollBodyDown();
        }
      }
    }
  }
});
